import { type LucideIcon } from 'lucide-react'

import { Checkbox } from '#app/components/ui/checkbox.tsx'
import { ListItem } from '#app/components/ui/list/item.tsx'
import { cn } from '#app/utils/misc.tsx'

type Option = {
	readonly value: string
	readonly label: string
}

type Props = {
	readonly option: Option
	readonly selected: boolean
	readonly icon?: LucideIcon
	readonly subtitle?: string | null
	readonly subtitleClassName?: string
	readonly errors?: readonly string[]
	readonly onPress?: () => void
	readonly onToggle?: (nextSelected: boolean) => void
	readonly toggleOnPress?: boolean
	readonly disableDeselect?: boolean
}

export function IntakeChannelCard({
	option,
	selected,
	icon,
	subtitle,
	subtitleClassName,
	errors = [],
	onPress,
	onToggle,
	toggleOnPress = false,
	disableDeselect = false,
}: Props) {
	const IconComponent = icon
	const hasErrors = errors.length > 0

	const handlePress = () => {
		if (toggleOnPress && onToggle) {
			if (selected && disableDeselect) return
			onToggle(!selected)
		}
		onPress?.()
	}

	const handleToggle = (nextSelected: boolean) => {
		if (nextSelected === false && disableDeselect) return
		onToggle?.(nextSelected)
	}

	const listItemClassName = cn(
		'transition-shadow',
		selected ? 'border-primary text-foreground bg-neutral-100/50' : '',
		hasErrors &&
			'border-destructive ring-destructive/40 text-destructive ring-1',
	)

	const selectionControl = onToggle ? (
		<Checkbox
			className="self-center"
			checked={selected}
			onCheckedChange={(checked) => {
				if (checked !== true && checked !== false) return
				handleToggle(checked)
			}}
			onClick={(event) => {
				event.stopPropagation()
			}}
			aria-label={
				selected
					? `Unselect ${option.label} intake channel`
					: `Select ${option.label} intake channel`
			}
		/>
	) : null

	return (
		<li className="space-y-1.5">
			<ListItem
				className={listItemClassName}
				innerClassName="items-center"
				onClick={handlePress}
				ariaPressed={selected}
				avatar={{
					alt: option.label,
					fallback: IconComponent ? (
						<IconComponent
							className={cn(
								'h-4 w-4',
								selected ? 'text-primary' : 'text-muted-foreground',
							)}
							aria-hidden="true"
						/>
					) : undefined,
				}}
				title={option.label}
				subtitle={subtitle}
				subtitleClassName={cn('line-clamp-1 text-xs', subtitleClassName)}
				actions={
					selectionControl ? (
						<div className="flex items-center">{selectionControl}</div>
					) : null
				}
			/>
			{hasErrors ? (
				<ul className="text-destructive list-disc pl-5 text-xs">
					{errors.map((error) => (
						<li key={error}>{error}</li>
					))}
				</ul>
			) : null}
		</li>
	)
}
